// Satellites — 4 project satellites orbiting the planet. Each satellite
// is a small icosahedron whose position is computed every frame from
// its orbit config (radius / speed / phase). The group lives inside
// the planet-world group so it rotates with the planet.
//
// Interaction is wired via R3F's built-in pointer events (which raycast
// under the hood); the SatelliteInteraction module mirrors hover state
// onto window.__planetHover for the HUD layer.
//
// Active satellite (when open via click): scales 1.5x and lerps its
// position toward cameraPos * 0.5 so it floats in front of the camera.
// Hover: scales 1.2x and brightens color.

import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

import { SATELLITES, type SatelliteConfig } from './planetSections';
import { openDetail, registerPickables } from './SatelliteInteraction';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

const HOVER_SCALE = 1.2;
const ACTIVE_SCALE = 1.5;
const ORBIT_Y = 0.2; // elevation of the orbital plane
const BASE_EMISSIVE_FACTOR = 0.4; // emissive = color * this
const HOVER_EMISSIVE_FACTOR = 0.75; // brightens on hover
const ACTIVE_EMISSIVE_FACTOR = 1.1; // even brighter when active

// Lerp speeds (per second, 0..1). 1 = instant. Higher = snappier.
const LERP_HOVER = 8;
const LERP_ACTIVE = 4;
const LERP_LEAVE = 3;

// ---------------------------------------------------------------------------
// Per-satellite scratch state — held outside React to keep frame-loop
// writes off the render path.
// ---------------------------------------------------------------------------

interface SatelliteRuntime {
  /** Current visual scale (eased toward target). */
  scale: number;
  /** Current lerped local position [x, y, z]. */
  position: THREE.Vector3;
  /** Base orbit position for the current frame, before any active-lerp. */
  basePosition: THREE.Vector3;
  /** Whether this satellite is the actively-opened one. */
  active: boolean;
  /** Whether this satellite is currently hovered. */
  hovered: boolean;
}

function makeRuntime(): SatelliteRuntime {
  return {
    scale: 1,
    position: new THREE.Vector3(),
    basePosition: new THREE.Vector3(),
    active: false,
    hovered: false,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SatellitesProps {
  /** When true, satellites render their labels at full opacity. */
  labelsVisible?: boolean;
}

export function Satellites({ labelsVisible = true }: SatellitesProps) {
  const camera = useThree((s) => s.camera) as THREE.Camera;
  const groupRef = useRef<Group | null>(null);
  const runtimesRef = useRef<SatelliteRuntime[]>([]);
  const meshRefs = useRef<(Mesh | null)[]>([]);
  const labelRefs = useRef<(Group | null)[]>([]);

  // Lazily initialize runtimes once per satellite.
  if (runtimesRef.current.length !== SATELLITES.length) {
    runtimesRef.current = SATELLITES.map(() => makeRuntime());
  }

  // Reusable scratch vectors (allocate once).
  const scratch = useMemo(
    () => ({
      camLocal: new THREE.Vector3(),
      basePos: new THREE.Vector3(),
      targetPos: new THREE.Vector3(),
      tmpColor: new THREE.Color(),
      tmpEmissive: new THREE.Color(),
    }),
    [],
  );

  // -------------------------------------------------------------------------
  // Publish the satellites group as the "interactive" group on window.
  // The HUD/raycaster reads this when it needs to scope raycasts.
  // Also register the meshes with SatelliteInteraction so the CPU
  // raycaster has a tight list to iterate.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Satellites are the primary interactive target; merge group exposed
    // here so the raycaster picks them up.
    window.__planetInteractiveGroup = groupRef.current;

    // Register the meshes with the raycaster. We do this on a rAF so
    // the mesh refs have settled by the time we read them.
    const raf = requestAnimationFrame(() => {
      const meshes = meshRefs.current
        .filter((m): m is Mesh => !!m)
        .map((mesh, i) => ({
          mesh,
          kind: 'satellite' as const,
          id: SATELLITES[i].id,
        }));
      if (meshes.length > 0) registerPickables(meshes);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (window.__planetInteractiveGroup === groupRef.current) {
        window.__planetInteractiveGroup = null;
      }
      // Unregister pickables.
      const meshes = meshRefs.current
        .filter((m): m is Mesh => !!m)
        .map((mesh, i) => ({
          mesh,
          kind: 'satellite' as const,
          id: SATELLITES[i].id,
        }));
      if (meshes.length > 0 && typeof window !== 'undefined') {
        const w = window as Window & { __planetPickables?: { mesh: Mesh; kind: string; id: string | number }[] };
        if (w.__planetPickables) {
          w.__planetPickables = w.__planetPickables.filter(
            (p) => !(p.kind === 'satellite' && meshes.some((m) => m.id === p.id)),
          );
        }
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // Frame loop: orbit, hover/active easing, label visibility.
  // -------------------------------------------------------------------------
  useFrame((state, delta) => {
    const dt = Math.min(delta, 1 / 30);
    const t = state.clock.elapsedTime;
    const cam = scratch.camLocal;

    // Camera position in world space. The world group has no offset, so
    // world == local for our orbit positions. We use this when an active
    // satellite is lerping toward "cameraPos * 0.5".
    cam.set(
      camera.position.x,
      camera.position.y,
      camera.position.z,
    );

    for (let i = 0; i < SATELLITES.length; i++) {
      const sat = SATELLITES[i];
      const rt = runtimesRef.current[i];
      const mesh = meshRefs.current[i];
      if (!mesh || !rt) continue;

      // 1) Compute the base orbit position (local space, at the
      //    satellite's orbitRadius / orbitSpeed / orbitPhase).
      const angle = t * sat.orbitSpeed + sat.orbitPhase;
      scratch.basePos.set(
        Math.cos(angle) * sat.orbitRadius,
        ORBIT_Y + Math.sin(angle * 0.5) * 0.05, // tiny vertical wobble
        Math.sin(angle) * sat.orbitRadius,
      );
      rt.basePosition.copy(scratch.basePos);

      // 2) Decide target scale + target position based on state.
      const targetScale = rt.active
        ? ACTIVE_SCALE
        : rt.hovered
          ? HOVER_SCALE
          : 1;
      const lerpRate = rt.active
        ? LERP_ACTIVE
        : rt.hovered
          ? LERP_HOVER
          : LERP_LEAVE;
      rt.scale += (targetScale - rt.scale) * Math.min(1, lerpRate * dt);

      if (rt.active) {
        // Lerp toward cameraPos * 0.5 (in local/world space, identical
        // because the world group has no offset).
        scratch.targetPos.copy(cam).multiplyScalar(0.5);
      } else {
        scratch.targetPos.copy(scratch.basePos);
      }
      rt.position.lerp(scratch.targetPos, Math.min(1, lerpRate * dt));
      mesh.position.copy(rt.position);

      // 3) Scale.
      mesh.scale.setScalar(rt.scale * sat.scale);

      // 4) Color / emissive. Brighten on hover and active.
      const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
      if (mat) {
        const emissiveFactor = rt.active
          ? ACTIVE_EMISSIVE_FACTOR
          : rt.hovered
            ? HOVER_EMISSIVE_FACTOR
            : BASE_EMISSIVE_FACTOR;
        scratch.tmpColor.set(sat.color);
        scratch.tmpEmissive.set(sat.color).multiplyScalar(emissiveFactor);
        mat.color.copy(scratch.tmpColor);
        mat.emissive.copy(scratch.tmpEmissive);
        mat.emissiveIntensity = 1;
      }

      // 5) Label opacity — fade in/out on hover/active. The CSS class
      //    toggle below handles the visual.
      const labelEl = labelRefs.current[i];
      if (labelEl) {
        const targetOpacity = rt.active ? 1 : rt.hovered ? 1 : labelsVisible ? 0.7 : 0.4;
        const cur = labelEl.userData.__opacity ?? targetOpacity;
        const next = cur + (targetOpacity - cur) * Math.min(1, 6 * dt);
        labelEl.userData.__opacity = next;
        // Children of the label group are HTMLElements; opacity is set
        // on each.
        labelEl.children.forEach((child) => {
          const el = child as unknown as HTMLElement;
          if (el && el.style) el.style.opacity = String(next);
        });
      }
    }
  });

  // -------------------------------------------------------------------------
  // Pointer handlers — these are the primary hover/click source.
  // They update window.__planetHover and window.__planetOpenPanelId,
  // which the SatelliteInteraction module and HUD layer both read.
  // -------------------------------------------------------------------------
  const handlePointerOver = (sat: SatelliteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      const rt = runtimesRef.current[i];
      if (rt) rt.hovered = true;
      if (typeof window !== 'undefined') {
        window.__planetHover = { kind: 'satellite', id: sat.id };
      }
      document.body.style.cursor = 'pointer';
    };

  const handlePointerOut = (sat: SatelliteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      const rt = runtimesRef.current[i];
      if (rt) rt.hovered = false;
      if (typeof window !== 'undefined') {
        const cur = window.__planetHover;
        if (cur && cur.kind === 'satellite' && cur.id === sat.id) {
          window.__planetHover = null;
        }
      }
      document.body.style.cursor = '';
    };

  const handleClick = (sat: SatelliteConfig, i: number) =>
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      // Exploration mode: fly to the satellite, show info card.
      if (typeof window !== 'undefined' && window.__planetMode === 'exploration') {
        const mesh = meshRefs.current[i];
        if (mesh && window.__planetShowInfoCard) {
          const wp = new THREE.Vector3();
          mesh.getWorldPosition(wp);
          window.__planetShowInfoCard({
            kind: 'satellite',
            id: sat.id,
            title: sat.name,
            blurb: sat.blurb,
            worldPos: [wp.x, wp.y, wp.z],
          });
        }
        return;
      }
      // Toggle active state — only one satellite active at a time.
      for (let j = 0; j < runtimesRef.current.length; j++) {
        runtimesRef.current[j].active = j === i;
      }
      if (typeof window !== 'undefined') {
        window.__planetOpenPanelId = `satellite:${sat.id}`;
        // Open the deep-dive detail panel (project).
        openDetail({ type: 'project', id: sat.id });
        // Also dispatch the legacy events so the right-rail info card
        // (and any other consumer) stays in sync.
        window.dispatchEvent(
          new CustomEvent('planet:open-panel', { detail: `satellite:${sat.id}` }),
        );
        window.dispatchEvent(
          new CustomEvent('satellite:open', { detail: { id: sat.id } }),
        );
      }
    };

  return (
    <group ref={groupRef} name="satellites">
      {SATELLITES.map((sat, i) => (
        <group
          key={sat.id}
          name={`satellite-group-${sat.id}`}
          userData={{ kind: 'satellite', id: sat.id }}
        >
          {/* The mesh itself. Radius = sat.scale (≈0.14..0.18).
           *
           *  Geometry is always IcosahedronGeometry(_, 0) — the lowest
           *  possible detail — regardless of orbit radius. At scale
           *  ~0.15 it's barely bigger than a pixel-triangle at typical
           *  camera distances, so anything higher is wasted budget.
           *  We've added frustumCulled explicitly so each tiny mesh
           *  is dropped from the draw call when off-screen instead of
           *  leaking through Three.js's default culling path. */}
          <mesh
            ref={(node) => {
              meshRefs.current[i] = node;
            }}
            name={`satellite-${sat.id}`}
            userData={{ kind: 'satellite', id: sat.id }}
            onPointerOver={handlePointerOver(sat, i)}
            onPointerOut={handlePointerOut(sat, i)}
            onClick={handleClick(sat, i)}
            frustumCulled
          >
            <icosahedronGeometry args={[1, 0]} />
            <meshStandardMaterial
              color={sat.color}
              emissive={sat.color}
              emissiveIntensity={BASE_EMISSIVE_FACTOR}
              roughness={0.6}
              metalness={0.15}
              flatShading
              toneMapped
            />
          </mesh>

          {/* Floating label above the satellite.
              NOTE: Drei's <Html> was removed (Phase 14 regression —
              reads lod.levels before ref is attached under Suspense).
              We track the label anchor with a ref so a future
              CSS2D-style overlay can position DOM text against it. */}
          <group
            ref={(node) => {
              labelRefs.current[i] = node;
            }}
            name={`satellite-label-${sat.id}`}
            position={[0, 0.35, 0]}
          />
        </group>
      ))}
    </group>
  );
}

export default Satellites;

// ---------------------------------------------------------------------------
// Global typings
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    /** Currently hovered interactive object, if any. */
    __planetHover?:
      | { kind: 'satellite'; id: string }
      | { kind: 'note'; id: number }
      | { kind: 'landmark'; key: string }
      | null;
    /** Currently open panel id (matches SCENES keys OR `satellite:<id>`
     *  OR `note:<id>`). */
    __planetOpenPanelId?: string | null;
    /** Deep-dive detail panel value. */
    __planetOpenPanel?: {
      type: 'project' | 'note' | 'milestone' | 'about' | 'skills' | 'contact';
      id: string;
    } | null;
  }
}
